import { Component, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { FormArray, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTabGroup as MatTabGroup } from '@angular/material/legacy-tabs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge, tap, map, filter, switchMap, forkJoin, catchError, of, Observable } from 'rxjs';

import { TsSnackbarService } from 'tsui';
import { Store } from '@ngxs/store';
import { AccountService } from 'app/core';
import { SavedFilter, Team, User } from 'app/core/models';
import { AccountState } from 'app/state/account/state';
import { Pipelines } from 'app/state/pipelines/action';
import { SavedFilters } from 'app/state/saved-filters/action';
import { SavedFiltersState } from 'app/state/saved-filters/state';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Pipeline } from 'app/modules/projects/projects.types';
import { PipelineAddComponent } from 'app/modules/projects/modals/pipeline-add/pipeline-add.component';
import { ObjectGroup } from 'app/shared/models/object-group';
import { ObjectGroupService } from 'app/services/object-group.service';
import { ProjectsListService } from '../list.service';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { FeatureFlag } from '@core/enums';

@Component({
  selector: 'projects-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
})
export class ProjectsSettingsComponent implements OnInit {
  pipelines: Pipeline[];
  savedFilters: SavedFilter[];
  pipelinesForm: UntypedFormGroup;
  savedFiltersForm: UntypedFormGroup;
  viewGroups: ObjectGroup[];
  selectedViewGroup: ObjectGroup;
  teams: Team[];
  users: User[];

  private store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  savedFilters$: Observable<SavedFilter[]> = inject(Store).select(SavedFiltersState.getVisibleFilters);

  private get accountId(): number {
    return Number(this.store.selectSnapshot(AccountState.getAccount).id);
  }

  @ViewChild('taskTemplateTabGroup') private tabGroup: MatTabGroup;

  constructor(
    private readonly accountService: AccountService,
    private readonly projectsService: ProjectsService,
    private readonly matDialog: MatDialog,
    private readonly snackBar: TsSnackbarService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly objectGroupService: ObjectGroupService,
    private readonly projectsListService: ProjectsListService,
    private readonly confirmService: ConfirmService,
  ) {}

  ngOnInit(): void {
    this.accountService.teams$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((teams) => {
          this.teams = teams;
          return this.accountService.users$;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => {
        this.users = users;
      });

    this.buildForms();
    this.fetchSavedFilters();
    this.loadViews();
  }

  private buildForms(): void {
    this.pipelinesForm = this.formBuilder.group({
      pipelines: this.formBuilder.array([]),
      newPipeline: this.formBuilder.group({
        name: ['', Validators.required],
        pipeline_type: ['static'],
        pipeline_template_id: [''],
        visibility: ['public_visibility'],
        num_projects_user_has_access_to: [0],
      }),
    });

    this.savedFiltersForm = this.formBuilder.group({
      savedFilters: this.formBuilder.array([]),
    });
  }

  private fetchSavedFilters(): void {
    this.store.dispatch([SavedFilters.Get]);
    this.savedFilters$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((savedFilters: SavedFilter[]) => (this.savedFilters = savedFilters)),
        map(() => this.savedFilters.map(this.createSavedFilterFormGroup.bind(this))),
      )
      .subscribe((formGroups: UntypedFormGroup[]) => {
        const formArray = this.savedFiltersForm.get('savedFilters') as UntypedFormArray;
        formArray.clear();
        formGroups.forEach((group) => formArray.push(group));
      });
  }

  private createSavedFilterFormGroup(savedFilter: SavedFilter): UntypedFormGroup {
    return this.formBuilder.group({
      id: [savedFilter.id],
      name: [savedFilter.name, Validators.required],
    });
  }

  private loadViews(): void {
    this.store.dispatch([Pipelines.Get, Pipelines.GetGroups]);

    this.objectGroupService
      .get(this.accountId, 'Pipeline')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((objectGroups) => {
          this.viewGroups = objectGroups;
          this.projectsListService.viewsGroups = objectGroups;
          this.viewGroups.push({ id: null, label: 'No Group' });
          if (!this.selectedViewGroup) this.selectedViewGroup = this.viewGroups[0];
          else {
            this.selectedViewGroup = this.viewGroups.find((g) => g.id === this.selectedViewGroup.id);
          }

          return this.projectsService.getPipelinesWithoutCache(true);
        }),
      )
      .subscribe((pipelines) => {
        (this.pipelinesForm.get('pipelines') as UntypedFormArray).clear();
        if (pipelines) {
          let memberIds = this.selectedViewGroup?.members?.map((m) => m.groupable_id);
          if (this.selectedViewGroup?.id === null) {
            memberIds = pipelines.filter((p) => !p?.object_group_id).map((p) => p.id);
          }
          const filteredPipelines = pipelines.filter((p) => memberIds?.includes(p.id));
          this.pipelines = filteredPipelines.sort((a, b) => a.rank - b.rank);

          filteredPipelines.forEach((pipeline) => {
            const pipelineFormGroup = this.formBuilder.group({
              id: [pipeline.id],
              name: [pipeline.name, Validators.required],
              pipeline_type: [pipeline?.pipeline_type],
              visibility: [pipeline.visibility],
              num_projects_user_has_access_to: [pipeline.num_projects_user_has_access_to],
              record_owner_id: [pipeline.record_owner_id || ''],
              team_id: [pipeline.team_id || ''],
              is_editable: [pipeline?.permissions?.is_editable],
              is_deletable: [pipeline?.permissions?.is_deletable],
            });

            (this.pipelinesForm.get('pipelines') as UntypedFormArray).push(pipelineFormGroup);
          });
        }
      });
  }

  private updateView(pipelineFormGroup: FormArray): void {
    const pipeline: Pipeline = {
      id: pipelineFormGroup.value.id,
      name: pipelineFormGroup.value.name,
      visibility: pipelineFormGroup.get('visibility').value,
    };
    this.projectsService
      .updatePipeline(pipeline)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadViews());
  }

  public updateMember(): void {
    (this.pipelinesForm.get('pipelines') as UntypedFormArray).controls.forEach((pipelineFormGroup) => {
      if (pipelineFormGroup.dirty) {
        let pipeline = {
          id: pipelineFormGroup.value.id,
          name: pipelineFormGroup.value.name,
          visibility: pipelineFormGroup.get('visibility').value,
        };
        this.projectsService
          .updatePipeline(pipeline)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loadViews();
              this.snackBar.open('View name updated successfully.');
            },
            error: () => this.snackBar.open('Unable to update the view name.', 'error'),
          });
      }
    });
    this.pipelinesForm.get('pipelines').markAsPristine();
    this.pipelinesForm.get('pipelines').markAsUntouched();
  }

  public updatedSavedFilter(): void {
    const dirtyControls = (this.savedFiltersForm.get('savedFilters') as UntypedFormArray).controls
      .filter((control) => control.dirty)
      .map((control) => ({
        id: control.value.id,
        name: control.value.name,
      }));

    if (dirtyControls.length === 0) {
      return;
    }

    forkJoin(
      dirtyControls.map((savedFilter) =>
        this.accountService.updateFilter(savedFilter).pipe(catchError(() => of(null))),
      ),
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.store.dispatch(new SavedFilters.Get())),
        tap(() => {
          this.savedFiltersForm.get('savedFilters').markAsPristine();
          this.savedFiltersForm.get('savedFilters').markAsUntouched();
        }),
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Saved filter name updated successfully.', 'success');
        },
        error: () => {
          this.snackBar.open('Unable to update one or more saved filter names.', 'error');
        },
      });
  }

  public isViewsTabActive(): boolean {
    return this.tabGroup ? this.tabGroup.selectedIndex === 0 : false;
  }

  public createMember(): void {
    this.matDialog
      .open(PipelineAddComponent, {
        data: {
          pipelines: this.pipelines,
          creatingView: true,
          selectedViewGroup: this.selectedViewGroup,
          users: this.users,
          teams: this.teams,
        },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => this.loadViews());
  }

  public createGroup(): void {
    this.matDialog
      .open(PipelineAddComponent, {
        data: { pipelines: this.pipelines, creatingGroup: true, users: this.users, teams: this.teams },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data) => !!data),
      )
      .subscribe((result: { refresh: boolean; newGroup: ObjectGroup }) => {
        if (result?.newGroup) this.selectViewGroup(result.newGroup);
      });
  }

  public moveMemberToGroup(pipeline: Pipeline): void {
    this.matDialog
      .open(PipelineAddComponent, {
        data: {
          pipeline,
          editingView: true,
          selectedViewGroup: this.selectedViewGroup,
          users: this.users,
          teams: this.teams,
        },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => this.loadViews());
  }

  public editGroup(): void {
    this.matDialog
      .open(PipelineAddComponent, {
        data: {
          pipelines: this.pipelines,
          editingGroup: true,
          selectedViewGroup: this.selectedViewGroup,
          users: this.users,
          teams: this.teams,
        },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => this.loadViews());
  }

  public reorderGroups(): void {
    this.matDialog
      .open(PipelineAddComponent, {
        data: { reorderingGroups: true, users: this.users, teams: this.teams },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => this.loadViews());
  }

  public deleteMember(id: number): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((res) => !!res),
      )
      .subscribe(() => {
        const oldMember = this.selectedViewGroup?.members?.find((m) => m.groupable_id === id);

        if (oldMember) {
          this.objectGroupService
            .deleteMember(this.accountId, this.selectedViewGroup.id, oldMember.id)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              switchMap(() => this.projectsService.deletePipeline(id)),
            )
            .subscribe(() => this.loadViews());
        } else {
          this.projectsService
            .deletePipeline(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.loadViews());
        }
      });
  }

  public deleteGroup(objectGroupId: number) {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((res) => !!res),
        switchMap(() => this.objectGroupService.delete(this.accountId, objectGroupId)),
      )
      .subscribe(() => {
        this.selectedViewGroup = null;
        this.loadViews();
      });
  }

  public deleteSavedFilter(id: number): void {
    this.confirmService
      .openConfirmationModal({ message: 'Are you sure that you wish to remove the saved filter?' })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this.accountService
          .deleteFilter(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.store.dispatch([SavedFilters.Get]);
            this.snackBar.open('Saved filter has been deleted.', 'success');
            const savedFiltersFormArray = this.savedFiltersForm.get('savedFilters') as UntypedFormArray;

            savedFiltersFormArray.removeAt(
              savedFiltersFormArray.value.findIndex((savedFilter: SavedFilter) => savedFilter.id === id),
            );
          });
      });
  }

  public toggleViewVisibility(pipeline: FormArray, visibility: string): void {
    pipeline.get('visibility').setValue(visibility);
    this.updateView(pipeline);
  }

  public dropped(event: CdkDragDrop<UntypedFormGroup[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

    const formGroups = event.container.data;
    const newPipelineRanks = formGroups.map((formGroup, index) => {
      const pipeline = formGroup.value;
      pipeline.rank = index + 1;
      return pipeline;
    });

    merge(...newPipelineRanks.map((pipeline) => this.projectsService.updatePipeline(pipeline)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.store.dispatch([Pipelines.Get, Pipelines.GetGroups]));
  }

  public getViewIcon(pipelineFormGroup: UntypedFormGroup): string {
    const pipeline = this.pipelines.find(({ id }) => id === pipelineFormGroup.value.id);
    return pipeline?.pipeline_type === 'static' ? 'list_alt' : 'low_priority';
  }

  selectViewGroup(viewGroup: ObjectGroup) {
    this.selectedViewGroup = viewGroup;
    this.loadViews();
  }

  isViewDeleteBtnDisabled(pipeline: UntypedFormGroup): boolean {
    if (!this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return false;
    }

    return pipeline.get('is_deletable').value === false;
  }
}
